import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createContext, useContext, type ReactNode } from "react";
import { api, ApiError, refreshCsrf, type MeDto } from "./api/client";
import { clearLocalData } from "./offline/queue";
import { stopRealtime } from "./realtime/realtime";
import { disablePush } from "./notifications/push";

type Session = { me: MeDto; logout: () => Promise<void> };

const SessionContext = createContext<Session | null>(null);

export function useSession(): Session {
  const s = useContext(SessionContext);
  if (!s) throw new Error("Keine Sitzung");
  return s;
}

export function useMe() {
  return useQuery({
    queryKey: ["me"],
    queryFn: () => api<MeDto>("/api/v1/me"),
    retry: (count, e) => !(e instanceof ApiError && (e.status === 401 || e.status === 403)) && count < 2,
    staleTime: 60_000,
  });
}

export function SessionProvider({ me, children }: { me: MeDto; children: ReactNode }) {
  const qc = useQueryClient();
  const logout = async () => {
    try {
      // Gemeinsam genutzte Geräte: nach dem Abmelden keine Erinnerungen dieses Kontos mehr.
      await disablePush().catch(() => undefined);
      await api("/api/v1/auth/logout", { method: "POST" });
    } finally {
      stopRealtime();
      await clearLocalData();
      qc.clear();
      await refreshCsrf();
      window.location.assign("/login");
    }
  };
  return <SessionContext.Provider value={{ me, logout }}>{children}</SessionContext.Provider>;
}

export async function login(loginName: string, password: string): Promise<void> {
  await refreshCsrf();
  await api("/api/v1/auth/login", { body: { login: loginName.trim(), password } });
  await refreshCsrf();
}
